import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbDownIcon as HandThumbDownSolid,
  HandThumbUpIcon as HandThumbUpSolid,
} from '@heroicons/react/24/solid';
import { apiFetch } from '../api';
import { useAuth } from '../AuthContext';
import { usePublicI18n } from '../PublicI18nContext';
import { TURNSTILE_SITE_KEY } from '../config';
import CodeDrawerLink from './Codes/CodeDrawerLink';
import TurnstileWidget from './TurnstileWidget';
import {
  cleanResponseText,
  isSafeInternalPath,
  isSafeWebHref,
  processMessageContent,
  safeUrlTransform,
} from './assistantMessage';

const THREAD_STORAGE_KEY = 'civicode.publicChatThread';

function readStoredThreadId() {
  try {
    const stored = window.sessionStorage.getItem(THREAD_STORAGE_KEY);
    return stored && stored.startsWith('thread_') ? stored : null;
  } catch (error) {
    return null;
  }
}

function storeThreadId(threadId) {
  try {
    if (threadId) window.sessionStorage.setItem(THREAD_STORAGE_KEY, threadId);
    else window.sessionStorage.removeItem(THREAD_STORAGE_KEY);
  } catch (error) {
    // Session storage unavailable (private mode); the thread just won't persist.
  }
}

/**
 * Resident-facing AI chat pane, rendered inside PublicHelpWidget's
 * "Ask a question" tab. Anonymous-capable: unauthenticated visitors may need
 * to pass a Turnstile check before their first message.
 */
export default function PublicAssistantChat({ onSwitchToStaff }) {
  const { user } = useAuth();
  const { t } = usePublicI18n();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(readStoredThreadId);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const messagesEndRef = useRef(null);

  const isAnonymous = !user;
  const needsTurnstile = Boolean(TURNSTILE_SITE_KEY) && isAnonymous && !threadId;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  const readErrorMessage = useCallback(async (response) => {
    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      // Fall through to status-specific defaults.
    }
    if (response.status === 503) return t('helpWidget.errorDisabled');
    if (response.status === 429) {
      return typeof payload?.detail === 'string' && payload.detail.includes('conversation')
        ? payload.detail
        : t('helpWidget.errorRateLimit');
    }
    if (response.status === 504) return t('helpWidget.errorTimeout');
    if (response.status === 400 && typeof payload?.detail === 'string') return payload.detail;
    return t('helpWidget.errorGeneric');
  }, [t]);

  const markdownComponents = useMemo(() => ({
    p: ({ node, ...props }) => <p className="mb-1 last:mb-0" {...props} />,
    a: ({ children, href }) => {
      if (href?.startsWith('code://')) {
        const codeId = href.replace('code://', '');
        if (!/^\d+$/.test(codeId)) return <span>{children}</span>;
        return (
          <CodeDrawerLink codeId={codeId} className="font-semibold text-brand hover:underline cursor-pointer">
            {children}
          </CodeDrawerLink>
        );
      }
      if (href?.startsWith('navigate://')) {
        const navPath = href.replace('navigate://', '');
        if (!isSafeInternalPath(navPath)) return <span>{children}</span>;
        return (
          <button
            type="button"
            onClick={() => navigate(navPath)}
            className="my-1 inline-flex cursor-pointer items-center gap-1 rounded-pill border border-brand/30 bg-brand-tint px-3 py-1 text-sm font-medium text-brand transition-colors hover:border-brand/50"
          >
            <span aria-hidden="true">&rarr;</span>
            {children}
          </button>
        );
      }
      if (!href || !isSafeWebHref(href)) return <span>{children}</span>;
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-brand underline">
          {children}
        </a>
      );
    },
  }), [navigate]);

  const startNewConversation = useCallback(() => {
    setThreadId(null);
    storeThreadId(null);
    setMessages([]);
    setTurnstileReset((n) => n + 1);
  }, []);

  const sendFeedback = useCallback(async (messageIndex, feedback) => {
    const message = messages[messageIndex];
    if (!message?.logId || !message?.threadId || message.feedback) return;
    setMessages((current) => current.map((item, index) => (
      index === messageIndex ? { ...item, feedback } : item
    )));
    try {
      await apiFetch('/assistant/public-chat/feedback', {
        method: 'POST',
        body: { logId: message.logId, threadId: message.threadId, feedback },
      });
    } catch (error) {
      // Feedback is best-effort; keep the optimistic UI state.
    }
  }, [messages]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage || isLoading) return;
    if (needsTurnstile && !turnstileToken) return;

    setMessages((current) => [...current, { role: 'user', content: trimmedMessage }]);
    setInputValue('');
    setIsLoading(true);

    const payload = { message: trimmedMessage };
    if (threadId) payload.threadId = threadId;
    if (needsTurnstile && turnstileToken) payload.turnstile_token = turnstileToken;

    try {
      const response = await apiFetch('/assistant/public-chat', {
        method: 'POST',
        body: payload,
      });

      if (!response.ok) {
        // Turnstile tokens are single-use; force a fresh challenge after any rejection.
        if (response.status === 400) {
          setTurnstileToken(null);
          setTurnstileReset((n) => n + 1);
        }
        // A stale stored thread (expired, or from another account) would otherwise
        // wedge the widget — drop it so the next send starts a fresh conversation.
        if (response.status === 404 && threadId) {
          setThreadId(null);
          storeThreadId(null);
          setTurnstileReset((n) => n + 1);
        }
        throw new Error(await readErrorMessage(response));
      }

      const responseData = await response.json();
      const { reply, threadId: nextThreadId, logId } = responseData ?? {};

      let effectiveThreadId = threadId;
      if (typeof nextThreadId === 'string' && nextThreadId.startsWith('thread_')) {
        effectiveThreadId = nextThreadId;
        setThreadId(nextThreadId);
        storeThreadId(nextThreadId);
      }

      const assistantContent =
        typeof reply === 'string' && reply.trim().length > 0
          ? cleanResponseText(reply)
          : t('helpWidget.errorGeneric');

      setMessages((current) => [...current, {
        role: 'assistant',
        content: assistantContent,
        logId: typeof logId === 'number' ? logId : null,
        threadId: effectiveThreadId,
      }]);
    } catch (error) {
      setMessages((current) => [...current, {
        role: 'assistant',
        content: error?.message || t('helpWidget.errorGeneric'),
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, needsTurnstile, turnstileToken, threadId, readErrorMessage, t]);

  const canSubmit = !isLoading && inputValue.trim().length > 0 && (!needsTurnstile || Boolean(turnstileToken));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div aria-live="polite" className="flex-1 space-y-3 overflow-y-auto rounded-card border border-gray-100 bg-white p-3">
        <div className="flex justify-start">
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-gray-100 px-3 py-2 text-sm leading-relaxed text-gray-900">
            {t('helpWidget.assistantGreeting')}
          </div>
        </div>

        {messages.map((message, index) => (
          <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="flex max-w-[85%] flex-col gap-1">
              <div
                className={`rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm ${
                  message.role === 'user'
                    ? 'rounded-tr-sm bg-brand text-brand-fg'
                    : message.isError
                      ? 'rounded-tl-sm bg-red-50 text-red-800'
                      : 'rounded-tl-sm bg-gray-100 text-gray-900'
                }`}
              >
                {message.role === 'assistant' && !message.isError ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    skipHtml
                    urlTransform={safeUrlTransform}
                    components={markdownComponents}
                  >
                    {processMessageContent(message.content)}
                  </ReactMarkdown>
                ) : (
                  <span className="whitespace-pre-wrap break-words">{message.content}</span>
                )}
              </div>

              {message.role === 'assistant' && !message.isError && message.logId && (
                <div className="flex items-center gap-2 px-1 text-[11px] text-gray-400">
                  {message.feedback ? (
                    <span>{t('helpWidget.feedbackThanks')}</span>
                  ) : (
                    <>
                      <span>{t('helpWidget.wasHelpful')}</span>
                      <button
                        type="button"
                        onClick={() => sendFeedback(index, 'up')}
                        className="rounded p-0.5 text-gray-500 hover:text-emerald-600"
                      >
                        <span className="sr-only">{t('helpWidget.wasHelpful')} - yes</span>
                        <HandThumbUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => sendFeedback(index, 'down')}
                        className="rounded p-0.5 text-gray-500 hover:text-red-600"
                      >
                        <span className="sr-only">{t('helpWidget.wasHelpful')} - no</span>
                        <HandThumbDownIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {message.feedback === 'up' && <HandThumbUpSolid className="h-4 w-4 text-emerald-600" />}
                  {message.feedback === 'down' && (
                    <>
                      <HandThumbDownSolid className="h-4 w-4 text-red-500" />
                      <button
                        type="button"
                        onClick={onSwitchToStaff}
                        className="font-semibold text-brand hover:underline"
                      >
                        {t('helpWidget.talkToStaff')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-tl-sm bg-gray-100 px-3 py-2 text-sm text-gray-500">
              {t('helpWidget.assistantThinking')}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <p className="mt-2 px-1 text-[11px] leading-snug text-gray-400">
        {t('helpWidget.assistantDisclaimer')}{' '}
        <button type="button" onClick={onSwitchToStaff} className="font-semibold text-brand hover:underline">
          {t('helpWidget.talkToStaff')}
        </button>
      </p>

      {needsTurnstile && (
        <div className="mt-2">
          {!turnstileToken && (
            <p className="mb-1 px-1 text-xs text-gray-500">{t('helpWidget.turnstileHint')}</p>
          )}
          <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileReset} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-2 flex items-end gap-2">
        <label htmlFor="public-assistant-input" className="sr-only">{t('helpWidget.assistantPlaceholder')}</label>
        <textarea
          id="public-assistant-input"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSubmit(event);
            }
          }}
          rows={2}
          maxLength={1500}
          placeholder={t('helpWidget.assistantPlaceholder')}
          className="min-h-[48px] flex-1 resize-none rounded-card border-gray-200 text-sm focus:border-brand focus:ring-brand"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-brand-fg shadow-sm transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="sr-only">{t('helpWidget.assistantSend')}</span>
          <PaperAirplaneIcon className="h-5 w-5" />
        </button>
      </form>

      {(threadId || messages.length > 0) && (
        <button
          type="button"
          onClick={startNewConversation}
          className="mt-2 inline-flex items-center gap-1 self-start px-1 text-xs font-semibold text-gray-500 hover:text-brand"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" />
          {t('helpWidget.newConversation')}
        </button>
      )}
    </div>
  );
}
